import styled from '@emotion/styled';
import QRCode from 'react-qr-code';

import CopyFilledCurrentIcon from '@/assets/svg/ui-elements/copy-filled-current.svg';
import ShieldLockOrangeIcon from '@/assets/svg/ui-elements/shield-lock-orange.svg';
import { Button, forMobile, Typography } from '@/design-system';
import { theme } from '@/design-system/theme';
import { useCopy } from '@/global-hooks/use-copy';

import { ChainIcon, ChainId } from './chain-icon';
import { CHAINS } from './chains';

const QR_SIZE = 200;
const MOBILE_QR_SIZE = 176;

export interface ReceiveAddressProps {
  symbol: string;
  chainId: ChainId;
  address: string | undefined;
}

const Card = styled('div')(({ theme: t }) => ({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
  gap: t.spacing[24],
  padding: t.spacing[16],
  borderRadius: t.radius[12],
  backgroundColor: t.colors.background.overlay,
}));
const ChainRow = styled('div')(({ theme: t }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: t.spacing[8],
  flexWrap: 'wrap',
}));
const ChainChip = styled('span')(({ theme: t }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: t.spacing[8],
  padding: `${t.spacing[4]}px ${t.spacing[8]}px`,
  borderRadius: t.radius.full,
  backgroundColor: t.colors.info.subtle,
  whiteSpace: 'nowrap',
}));
const Warning = styled('div')(({ theme: t }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: t.spacing[12],
  boxSizing: 'border-box',
  padding: `${t.spacing[12]}px ${t.spacing[16]}px`,
  border: `1px solid ${t.colors.warning.border}`,
  borderRadius: t.radius[8],
  backgroundColor: t.colors.warning.subtle,
  '& svg': { width: 16, height: 16, flexShrink: 0, marginTop: 4 },
}));
const WarningText = styled('div')(({ theme: t }) => ({
  display: 'flex',
  minWidth: 0,
  flexDirection: 'column',
  gap: t.spacing[2],
}));
const QrFrame = styled('div')({
  display: 'flex',
  alignSelf: 'center',
  '& svg': {
    width: QR_SIZE,
    height: QR_SIZE,
    maxWidth: '100%',

    [forMobile]: {
      width: MOBILE_QR_SIZE,
      height: MOBILE_QR_SIZE,
    },
  },
});
const AddressGroup = styled('div')(({ theme: t }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: t.spacing[8],
}));
const AddressRow = styled('div')(({ theme: t }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: t.spacing[8],
  boxSizing: 'border-box',
  minHeight: 72,
  padding: t.spacing[12],
  borderRadius: t.radius[16],
  backgroundColor: t.colors.background.raised,
}));
const Address = styled('span')(({ theme: t }) => ({
  flex: 1,
  minWidth: 0,
  fontFamily: t.typography.fontFamily.mono,
  fontSize: 14,
  lineHeight: '24px',
  color: t.colors.text.primary,
  overflowWrap: 'anywhere',
}));
const CopyButton = styled(Button)({ flexShrink: 0, [forMobile]: { minHeight: 44 } });

const CopyAddressButton = ({ address }: { address: string }) => {
  const { isTooltipOpen, handleClick } = useCopy({ textToCopy: address });
  return (
    <CopyButton
      size="md"
      color="tertiary"
      variant="solid"
      onClick={handleClick}
      aria-label="Copy address"
    >
      <CopyFilledCurrentIcon aria-hidden="true" />
      {isTooltipOpen ? 'Copied!' : 'Copy'}
    </CopyButton>
  );
};

export const ReceiveAddress = ({ symbol, chainId, address }: ReceiveAddressProps) => {
  const chain = CHAINS[chainId];
  if (!address)
    return (
      <Typography variant="body2" color={theme.colors.text.secondary} align="center">
        No wallet address available for the {chain.label} network.
      </Typography>
    );
  return (
    <Card>
      <ChainRow>
        <Typography as="span" variant="body1" fontWeight={500} color={theme.colors.text.primary}>
          Receive {symbol} on the
        </Typography>
        <ChainChip>
          <ChainIcon chainId={chainId} size={24} />
          <Typography as="span" variant="body1" fontWeight={500} color={theme.colors.text.primary}>
            {chain.label} Chain
          </Typography>
        </ChainChip>
      </ChainRow>
      <Warning>
        <ShieldLockOrangeIcon aria-hidden="true" />
        <WarningText>
          <Typography variant="body2" fontWeight={500} color={theme.colors.text.primary}>
            Only send assets on the {chain.label} chain to this address.
          </Typography>
          <Typography variant="body3" color={theme.colors.text.secondary}>
            Sending from other chains will result in lost funds.
          </Typography>
        </WarningText>
      </Warning>
      <QrFrame>
        <QRCode
          value={address}
          size={QR_SIZE}
          bgColor="transparent"
          fgColor={theme.colors.text.primary}
        />
      </QrFrame>
      <AddressGroup>
        <Typography variant="body3" color={theme.colors.text.primary}>
          Send {symbol} to this address. It will be added to your balance on Gamma Prime.
        </Typography>
        <AddressRow>
          <Address>{address}</Address>
          <CopyAddressButton address={address} />
        </AddressRow>
      </AddressGroup>
    </Card>
  );
};
